import { create } from 'zustand';
import {
  CANVAS_CONFIG,
  CANVAS_EXPANSION_CONFIG,
  COLOR_PALETTE,
  type PaletteColor,
  type PixelMeta,
} from '@the-wall/shared';
import { computeZoomAtPoint } from '@/utils/canvasZoom';

// Minimum percentage of canvas that must remain visible
const MIN_VISIBLE_PERCENT = 0.75; // Keep at least 75% of canvas visible (desktop)
const MIN_VISIBLE_PERCENT_MOBILE = 0.1; // On mobile, allow more pan so right edge can be painted (menus on right)
const MOBILE_VIEWPORT_MAX_WIDTH = 640; // Match Tailwind sm: breakpoint

interface CanvasState {
  // Canvas data
  pixels: (PaletteColor | null)[][];
  pixelMeta: Record<string, PixelMeta>; // key: "x,y"
  width: number;
  height: number;
  activePixels: number;
  totalPixels: number;
  completionRatio: number;
  expansionCount: number;
  completionThreshold: number;
  expandByPixels: number;

  // User interaction
  selectedColor: PaletteColor;

  // Viewport
  zoom: number;
  minZoom: number;
  panX: number;
  panY: number;
  viewportWidth: number;
  viewportHeight: number;

  // UI State
  hoveredPixel: { x: number; y: number } | null;
  inspectedPixel: { x: number; y: number } | null;
  isConnected: boolean;
  yoloMode: boolean;
  expansionCelebration: {
    id: number;
    previousWidth: number;
    previousHeight: number;
    width: number;
    height: number;
    expandedByPixels: number;
  } | null;

  // Overlay template (transparent guide for group pixel art)
  overlayPixels: (PaletteColor | null)[][] | null;
  /** Canvas region where overlay is drawn: top-left (x1,y1) to bottom-right (x2,y2) inclusive */
  overlayBounds: { x1: number; y1: number; x2: number; y2: number } | null;
  overlayVisible: boolean;
  overlayOpacity: number;

  // Actions
  setPixel: (x: number, y: number, color: PaletteColor, meta?: PixelMeta) => void;
  setSelectedColor: (color: PaletteColor) => void;
  setZoom: (zoom: number) => void;
  setZoomAtPoint: (newZoom: number, pointX: number, pointY: number) => void;
  setZoomAndPan: (newZoom: number, newPanX: number, newPanY: number) => void;
  setMinZoom: (minZoom: number) => void;
  setPan: (x: number, y: number) => void;
  setViewportSize: (width: number, height: number) => void;
  setHoveredPixel: (pixel: { x: number; y: number } | null) => void;
  setInspectedPixel: (pixel: { x: number; y: number } | null) => void;
  setIsConnected: (connected: boolean) => void;
  setYoloMode: (enabled: boolean) => void;
  showExpansionCelebration: (event: {
    previousWidth: number;
    previousHeight: number;
    width: number;
    height: number;
    expandedByPixels: number;
  }) => void;
  clearExpansionCelebration: () => void;
  setOverlayPixels: (
    pixels: (PaletteColor | null)[][] | null,
    bounds?: { x1: number; y1: number; x2: number; y2: number } | null
  ) => void;
  setOverlayVisible: (visible: boolean) => void;
  setOverlayOpacity: (opacity: number) => void;
  clearOverlay: () => void;
  initializeCanvas: (
    pixels: (PaletteColor | null)[][],
    pixelMeta?: Record<string, PixelMeta>,
    metadata?: {
      width?: number;
      height?: number;
      activePixels?: number;
      totalPixels?: number;
      completionRatio?: number;
      expansionCount?: number;
      completionThreshold?: number;
      expandByPixels?: number;
      shiftOverlayBy?: number;
    }
  ) => void;
  getPixelMeta: (x: number, y: number) => PixelMeta | null;
  resetCanvas: (dimensions?: { width: number; height: number; expansionCount: number }) => void;
  navigateToCoordinate: (x: number, y: number) => void;
}

// Calculate max pan distance to keep at least minVisible of canvas on screen
function calculateMaxPan(
  canvasSize: number,
  viewportSize: number,
  zoom: number,
  viewportWidthForMobile?: number
): number {
  const isMobile =
    viewportWidthForMobile !== undefined && viewportWidthForMobile < MOBILE_VIEWPORT_MAX_WIDTH;
  const minVisible = isMobile ? MIN_VISIBLE_PERCENT_MOBILE : MIN_VISIBLE_PERCENT;

  const scaledCanvasSize = canvasSize * zoom;

  // When zoomed in (canvas bigger than viewport):
  // Keep at least minVisible of viewport filled with canvas
  const zoomedInLimit = (scaledCanvasSize - viewportSize * minVisible) / 2;

  // When zoomed out (canvas smaller than viewport):
  // Keep at least minVisible of canvas visible
  const zoomedOutLimit = ((1 - minVisible) * scaledCanvasSize) / 2;

  // Use whichever gives more freedom (handles transition smoothly)
  return Math.max(0, Math.max(zoomedInLimit, zoomedOutLimit));
}

function createEmptyCanvas(
  width: number = CANVAS_CONFIG.WIDTH,
  height: number = CANVAS_CONFIG.HEIGHT
): (PaletteColor | null)[][] {
  return Array.from({ length: height }, () => Array.from({ length: width }, () => null));
}

function getTotalPixels(width: number, height: number): number {
  return width * height;
}

function getCompletionRatio(activePixels: number, totalPixels: number): number {
  return totalPixels > 0 ? activePixels / totalPixels : 0;
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  pixels: createEmptyCanvas(),
  pixelMeta: {},
  width: CANVAS_CONFIG.WIDTH,
  height: CANVAS_CONFIG.HEIGHT,
  activePixels: 0,
  totalPixels: getTotalPixels(CANVAS_CONFIG.WIDTH, CANVAS_CONFIG.HEIGHT),
  completionRatio: 0,
  expansionCount: 0,
  completionThreshold: CANVAS_EXPANSION_CONFIG.COMPLETION_THRESHOLD,
  expandByPixels: CANVAS_EXPANSION_CONFIG.EXPAND_BY_PIXELS,
  selectedColor: COLOR_PALETTE[0] ?? '#FFFFFF',
  zoom: 1,
  minZoom: 1,
  panX: 0,
  panY: 0,
  viewportWidth: 800,
  viewportHeight: 600,
  hoveredPixel: null,
  inspectedPixel: null,
  isConnected: false,
  yoloMode: false,
  overlayPixels: null,
  overlayBounds: null,
  overlayVisible: false,
  overlayOpacity: 0.5,
  expansionCelebration: null,

  setPixel: (x, y, color, meta) =>
    set((state) => {
      if (x < 0 || x >= state.width || y < 0 || y >= state.height) {
        return {};
      }

      const newPixels = state.pixels.map((row) => [...row]);
      const row = newPixels[y];
      const previousColor = row?.[x] ?? null;
      if (row) row[x] = color;
      const activePixels = previousColor ? state.activePixels : state.activePixels + 1;
      const completionRatio = getCompletionRatio(activePixels, state.totalPixels);

      // Update metadata if provided
      if (meta) {
        const key = `${x},${y}`;
        return {
          pixels: newPixels,
          activePixels,
          completionRatio,
          pixelMeta: { ...state.pixelMeta, [key]: meta },
        };
      }

      return { pixels: newPixels, activePixels, completionRatio };
    }),

  setSelectedColor: (color) => set({ selectedColor: color }),

  // Enforce minimum zoom (80% viewport fill) and maximum zoom
  setZoom: (zoom) => {
    const { minZoom, panX, panY, viewportWidth, viewportHeight, width, height } = get();
    const newZoom = Math.max(minZoom, Math.min(CANVAS_CONFIG.MAX_ZOOM, zoom));

    // Re-clamp pan after zoom change (canvas size changes)
    const maxPanX = calculateMaxPan(width, viewportWidth, newZoom, viewportWidth);
    const maxPanY = calculateMaxPan(height, viewportHeight, newZoom, viewportWidth);

    set({
      zoom: newZoom,
      panX: Math.max(-maxPanX, Math.min(maxPanX, panX)),
      panY: Math.max(-maxPanY, Math.min(maxPanY, panY)),
    });
  },

  // Zoom towards a specific point (for scroll/pinch zoom) — same math as gallery via canvasZoom util
  setZoomAtPoint: (newZoom, pointX, pointY) => {
    const { minZoom, panX, panY, zoom } = get();
    const result = computeZoomAtPoint({
      zoom,
      panX,
      panY,
      pointX,
      pointY,
      newZoom,
      minZoom,
      maxZoom: CANVAS_CONFIG.MAX_ZOOM,
    });
    if (result) {
      set({ zoom: result.zoom, panX: result.panX, panY: result.panY });
    }
  },

  // Set zoom and pan atomically (for pinch zoom where pan is pre-calculated)
  setZoomAndPan: (newZoom, newPanX, newPanY) => {
    const { minZoom, zoom } = get();
    const clampedZoom = Math.max(minZoom, Math.min(CANVAS_CONFIG.MAX_ZOOM, newZoom));

    // If zoom didn't change (at min/max limit), don't update anything
    if (clampedZoom === zoom) {
      return;
    }

    // Don't clamp during zoom operations
    set({
      zoom: clampedZoom,
      panX: newPanX,
      panY: newPanY,
    });
  },

  // Update minimum zoom based on viewport size
  setMinZoom: (minZoom) => {
    const { zoom } = get();
    // Also update current zoom if it's below the new minimum
    if (zoom < minZoom) {
      set({ minZoom, zoom: minZoom });
    } else {
      set({ minZoom });
    }
  },

  // Set pan with limits to keep at least min visible % of canvas on screen
  setPan: (x, y) => {
    const { zoom, viewportWidth, viewportHeight, width, height } = get();

    const maxPanX = calculateMaxPan(width, viewportWidth, zoom, viewportWidth);
    const maxPanY = calculateMaxPan(height, viewportHeight, zoom, viewportWidth);

    set({
      panX: Math.max(-maxPanX, Math.min(maxPanX, x)),
      panY: Math.max(-maxPanY, Math.min(maxPanY, y)),
    });
  },

  // Update viewport size (called on resize)
  setViewportSize: (width, height) => {
    const { panX, panY, zoom, width: canvasWidth, height: canvasHeight } = get();

    // Re-clamp pan with new viewport size
    const maxPanX = calculateMaxPan(canvasWidth, width, zoom, width);
    const maxPanY = calculateMaxPan(canvasHeight, height, zoom, width);

    set({
      viewportWidth: width,
      viewportHeight: height,
      panX: Math.max(-maxPanX, Math.min(maxPanX, panX)),
      panY: Math.max(-maxPanY, Math.min(maxPanY, panY)),
    });
  },

  setHoveredPixel: (pixel) => set({ hoveredPixel: pixel }),
  setInspectedPixel: (pixel) => set({ inspectedPixel: pixel }),

  setIsConnected: (connected) => set({ isConnected: connected }),

  setYoloMode: (enabled) => set({ yoloMode: enabled }),

  showExpansionCelebration: (event) =>
    set({
      expansionCelebration: {
        id: Date.now(),
        ...event,
      },
    }),

  clearExpansionCelebration: () => set({ expansionCelebration: null }),

  setOverlayPixels: (pixels, bounds) =>
    set((s) => ({
      overlayPixels: pixels,
      overlayBounds: bounds !== undefined ? bounds : pixels ? s.overlayBounds : null,
    })),
  setOverlayVisible: (visible) => set({ overlayVisible: visible }),
  setOverlayOpacity: (opacity) => set({ overlayOpacity: Math.max(0, Math.min(1, opacity)) }),
  clearOverlay: () => set({ overlayPixels: null, overlayBounds: null, overlayVisible: false }),

  initializeCanvas: (pixels, pixelMeta, metadata) =>
    set((state) => {
      const width = metadata?.width ?? pixels[0]?.length ?? state.width;
      const height = metadata?.height ?? pixels.length ?? state.height;
      const totalPixels = metadata?.totalPixels ?? getTotalPixels(width, height);
      const activePixels =
        metadata?.activePixels ??
        pixels.reduce(
          (count, row) => count + row.reduce((rowCount, color) => rowCount + (color ? 1 : 0), 0),
          0
        );
      const shiftOverlayBy = metadata?.shiftOverlayBy ?? 0;
      const overlayBounds =
        shiftOverlayBy && state.overlayBounds
          ? {
              x1: state.overlayBounds.x1 + shiftOverlayBy,
              y1: state.overlayBounds.y1 + shiftOverlayBy,
              x2: state.overlayBounds.x2 + shiftOverlayBy,
              y2: state.overlayBounds.y2 + shiftOverlayBy,
            }
          : state.overlayBounds;

      return {
        pixels,
        pixelMeta: pixelMeta ?? {},
        width,
        height,
        activePixels,
        totalPixels,
        completionRatio: metadata?.completionRatio ?? getCompletionRatio(activePixels, totalPixels),
        expansionCount: metadata?.expansionCount ?? state.expansionCount,
        completionThreshold: metadata?.completionThreshold ?? state.completionThreshold,
        expandByPixels: metadata?.expandByPixels ?? state.expandByPixels,
        overlayBounds,
      };
    }),

  getPixelMeta: (x, y) => {
    const key = `${x},${y}`;
    return get().pixelMeta[key] ?? null;
  },

  resetCanvas: (dimensions) =>
    set((state) => {
      // A fresh round starts back at the configured base size, so honor the new
      // dimensions broadcast on reset (falling back to the current size if absent).
      const width = dimensions?.width ?? state.width;
      const height = dimensions?.height ?? state.height;
      return {
        pixels: createEmptyCanvas(width, height),
        pixelMeta: {},
        width,
        height,
        totalPixels: getTotalPixels(width, height),
        activePixels: 0,
        completionRatio: 0,
        expansionCount: dimensions?.expansionCount ?? state.expansionCount,
        hoveredPixel: null,
        inspectedPixel: null,
      };
    }),

  // Navigate to a specific pixel coordinate and zoom in
  navigateToCoordinate: (x, y) => {
    const { viewportWidth, viewportHeight, minZoom, width, height } = get();

    // Clamp coordinates to valid range
    const clampedX = Math.max(0, Math.min(width - 1, Math.floor(x)));
    const clampedY = Math.max(0, Math.min(height - 1, Math.floor(y)));

    // Calculate zoom level (10x for a good close-up view)
    const targetZoom = Math.max(minZoom, Math.min(CANVAS_CONFIG.MAX_ZOOM, 10));

    // Calculate pan to center the pixel
    // Pixel center in canvas space: (clampedX + 0.5, clampedY + 0.5)
    // Canvas center: (width / 2, height / 2)
    // Offset from canvas center: (clampedX + 0.5 - width / 2, clampedY + 0.5 - height / 2)
    // To center at viewport center (0, 0 in pan space), pan = -offset * zoom
    const PIXEL_SIZE = 1;
    const canvasCenterX = (width * PIXEL_SIZE) / 2;
    const canvasCenterY = (height * PIXEL_SIZE) / 2;
    const pixelX = (clampedX + 0.5) * PIXEL_SIZE;
    const pixelY = (clampedY + 0.5) * PIXEL_SIZE;

    const newPanX = -(pixelX - canvasCenterX) * targetZoom;
    const newPanY = -(pixelY - canvasCenterY) * targetZoom;

    // Clamp pan to valid bounds
    const maxPanX = calculateMaxPan(width, viewportWidth, targetZoom, viewportWidth);
    const maxPanY = calculateMaxPan(height, viewportHeight, targetZoom, viewportWidth);

    set({
      zoom: targetZoom,
      panX: Math.max(-maxPanX, Math.min(maxPanX, newPanX)),
      panY: Math.max(-maxPanY, Math.min(maxPanY, newPanY)),
    });
  },
}));
